/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Box, Text, Stack, Badge, Paper, Divider, Anchor, Tooltip, Group, Title } from '@mantine/core';
import {
  IconPlaneDeparture,
  IconPlaneArrival,
  IconCalendar,
  IconBulb,
  IconExternalLink,
  IconTransferVertical,
  IconStars,
  IconInfoCircle
} from '@tabler/icons-react';
import { coral } from 'theme/colors';
import { semanticColors } from 'theme/semanticColors';
import { shadows } from 'theme/shadows';
import { radius } from 'theme/radius';

const FlightOptionCard = ({ option, onScanPoints }) => {
  const { airlines, price_estimate, currency, price_source, suggested_departure, suggested_return, notes } = option;

  const currencySymbol = currency === 'USD' ? '$' : (currency ?? '');

  return (
    <Paper
      withBorder
      radius={radius.surface}
      style={{
        transition: 'border-color 0.2s, box-shadow 0.2s'
      }}
      styles={{
        root: {
          '&:hover': {
            borderColor: coral[6],
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
          }
        }
      }}
    >
      <Box p="sm">
        {/* Dates + Price */}
        <Group justify="space-between" align="flex-start">
          {/* Dates */}
          <Stack gap={8}>
            <Group gap={8} align="center">
              <IconPlaneDeparture size={14} color={coral[6]} />
              <Text size="sm" fw={400} style={{ letterSpacing: 0.2 }}>
                {dayjs(suggested_departure).format(' MMM DD, YYYY | ddd')}
              </Text>
            </Group>
            {suggested_return && (
              <Group gap={8} align="center">
                <IconPlaneArrival size={14} color={coral[6]} />
                <Text size="sm" fw={400} style={{ letterSpacing: 0.2 }}>
                  {dayjs(suggested_return).format(' MMM DD, YYYY | ddd')}
                </Text>
              </Group>
            )}
          </Stack>

          {/* Price */}
          <Box style={{ textAlign: 'right', paddingLeft: 16 }}>
            <Group gap={4} justify="flex-end" align="center">
              <Title order={3} c={coral[6]} style={{ lineHeight: 1 }}>
                {currencySymbol}
                {price_estimate}
              </Title>
              <Tooltip label="Prices are estimates and may change due to demand, availability, and timing." withArrow position="top">
                <IconInfoCircle size={14} color={semanticColors.text.muted} style={{ cursor: 'pointer' }} />
              </Tooltip>
            </Group>
            {price_source && (
              <Text size="xs" c={semanticColors.text.muted} style={{ display: 'block', marginTop: 2 }}>
                {price_source}
              </Text>
            )}
          </Box>
        </Group>

        {/* Notes */}
        {notes && (
          <Text size="xs" c={semanticColors.text.secondary} style={{ display: 'block', marginTop: 16, lineHeight: 1.6 }}>
            {notes}
          </Text>
        )}

        {/* Airline booking links */}
        {airlines?.length > 0 && (
          <>
            <Divider my="sm" />
            <Text size="xs" c={coral[6]} tt="uppercase" style={{ letterSpacing: 1, fontSize: '0.65rem' }}>
              Book via
            </Text>
            <Group gap={6} mt={6} wrap="wrap">
              {airlines.map(({ airline, airline_code, booking_link }) => (
                <Anchor key={airline_code} href={booking_link} target="_blank" rel="noopener noreferrer" underline="never">
                  <Badge
                    variant="outline"
                    size="sm"
                    radius={4}
                    leftSection={<IconExternalLink size={11} />}
                    style={{
                      fontSize: '0.72rem',
                      height: 26,
                      cursor: 'pointer',
                      color: semanticColors.text.primary,
                      borderColor: semanticColors.border.default
                    }}
                  >
                    {airline}
                  </Badge>
                </Anchor>
              ))}
            </Group>
          </>
        )}
        {onScanPoints && (
          <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Badge
              size="sm"
              variant="filled"
              leftSection={<IconStars size={13} color="white" />}
              onClick={onScanPoints}
              radius={6}
              style={{
                fontSize: '0.72rem',
                height: 26,
                cursor: 'pointer',
                backgroundColor: coral[6],
                color: 'white',
                borderColor: coral[6]
              }}
            >
              Scan Points
            </Badge>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

FlightOptionCard.propTypes = {
  option: PropTypes.shape({
    airlines: PropTypes.arrayOf(
      PropTypes.shape({
        airline: PropTypes.string,
        airline_code: PropTypes.string,
        booking_link: PropTypes.string
      })
    ),
    price_estimate: PropTypes.string,
    currency: PropTypes.string,
    price_source: PropTypes.string,
    suggested_departure: PropTypes.string,
    suggested_return: PropTypes.string,
    notes: PropTypes.string
  }).isRequired,
  onScanPoints: PropTypes.func
};

const SectionLabel = ({ icon, children }) => (
  <Group gap={8} align="center" mb={8}>
    {icon}
    <Text fw={600} c={semanticColors.text.primary}>
      {children}
    </Text>
  </Group>
);

SectionLabel.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired
};

const AISearchResponseContent = ({ data, onScanPoints }) => {
  const {
    origin,
    destination,
    date_from,
    date_to,
    route_summary,
    cheapest_days_to_fly,
    flight_options,
    best_time_to_book,
    money_saving_tips,
    booking_links
  } = data;

  const bookingLinkEntries = [
    { key: 'kayak_explore', label: 'Kayak Explore' },
    { key: 'kayak_cheapest', label: 'Kayak Cheapest' },
    { key: 'skyscanner_cheapest', label: 'Skyscanner' },
    { key: 'google_flights_cheapest', label: 'Google Flights' }
  ].filter(({ key }) => booking_links?.[key]);

  return (
    <Box>
      {/* Route header */}
      <Group gap={8} align="center" mb={4}>
        <Title order={4} c={coral[6]}>
          {origin}
        </Title>
        <IconTransferVertical size={20} color={semanticColors.text.secondary} />
        <Title order={4} c={coral[6]}>
          {destination}
        </Title>
      </Group>
      <Text size="xs" c={semanticColors.text.secondary}>
        {dayjs(date_from).format('MMM DD')} — {dayjs(date_to).format('MMM DD, YYYY')}
      </Text>

      <Divider my="md" />

      {/* Route summary */}
      <Text size="sm" c={semanticColors.text.secondary} style={{ lineHeight: 1.7, marginBottom: 24 }}>
        {route_summary}
      </Text>

      {/* Flight options */}
      {flight_options?.length > 0 && (
        <Box mb={24}>
          <SectionLabel icon={<IconPlaneDeparture size={20} color={coral[6]} />}>Flight Options</SectionLabel>
          <Stack gap={12}>
            {flight_options.map((option, index) => (
              <FlightOptionCard
                key={option.suggested_departure ?? index}
                option={option}
                onScanPoints={
                  onScanPoints ? () => onScanPoints({ origin, destination, date: option.suggested_departure ?? date_from }) : undefined
                }
              />
            ))}
          </Stack>
          <Box
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginTop: 16,
              padding: '12px 16px',
              borderRadius: 8,
              backgroundColor: 'rgba(255, 167, 38, 0.08)',
              border: '1px solid rgba(255, 167, 38, 0.2)'
            }}
          >
            <IconInfoCircle size={16} color={semanticColors.status.warning} style={{ marginTop: 2, flexShrink: 0 }} />
            <Text size="xs" c={semanticColors.text.secondary} style={{ lineHeight: 1.5 }}>
              Prices shown are estimates at the time this search was generated and may vary. Fares can change due to demand, availability,
              and timing — we recommend booking soon for the best deal.
            </Text>
          </Box>
        </Box>
      )}

      {/* Cheapest days */}
      {cheapest_days_to_fly?.length > 0 && (
        <Box mb={24}>
          <SectionLabel icon={<IconCalendar size={20} color={coral[6]} />}>Cheapest Days to Fly</SectionLabel>
          <Group gap={8} wrap="wrap">
            {cheapest_days_to_fly.map((day) => (
              <Badge key={day} variant="outline" size="sm" radius={6}>
                {day}
              </Badge>
            ))}
          </Group>
        </Box>
      )}

      {/* Best time to book */}
      {best_time_to_book && (
        <Box mb={24}>
          <SectionLabel icon={<IconPlaneArrival size={20} color={coral[6]} />}>Best Time to Book</SectionLabel>
          <Text size="sm" c={semanticColors.text.secondary}>
            {best_time_to_book}
          </Text>
        </Box>
      )}

      {/* Money saving tips */}
      {money_saving_tips?.length > 0 && (
        <Box mb={24}>
          <SectionLabel icon={<IconBulb size={20} color={coral[6]} />}>Money Saving Tips</SectionLabel>
          <Stack gap={6}>
            {money_saving_tips.map((tip, i) => (
              <Text key={i} size="sm" c={semanticColors.text.secondary} style={{ paddingLeft: 12 }}>
                &bull; &nbsp;{tip}
              </Text>
            ))}
          </Stack>
        </Box>
      )}

      {/* Booking links */}
      {bookingLinkEntries.length > 0 && (
        <Box>
          <Divider mb="md" />
          <Group gap={8} wrap="wrap">
            {bookingLinkEntries.map(({ key, label }) => (
              <Anchor key={key} href={booking_links[key]} target="_blank" rel="noopener noreferrer" underline="never">
                <Badge size="sm" leftSection={<IconExternalLink size={14} />} radius={6} style={{ cursor: 'pointer' }}>
                  {label}
                </Badge>
              </Anchor>
            ))}
          </Group>
        </Box>
      )}
    </Box>
  );
};

AISearchResponseContent.propTypes = {
  onScanPoints: PropTypes.func,
  data: PropTypes.shape({
    origin: PropTypes.string,
    destination: PropTypes.string,
    date_from: PropTypes.string,
    date_to: PropTypes.string,
    route_summary: PropTypes.string,
    cheapest_days_to_fly: PropTypes.arrayOf(PropTypes.string),
    flight_options: PropTypes.arrayOf(PropTypes.object),
    best_time_to_book: PropTypes.string,
    money_saving_tips: PropTypes.arrayOf(PropTypes.string),
    booking_links: PropTypes.object
  }).isRequired
};

export default AISearchResponseContent;
